// Cloudinary helpers for URL generation and scoped image listing
import { API_BASE_URL } from './apiBase';

export interface CloudinaryImage {
  publicId: string;
  public_id?: string;
  secure_url?: string;
  url?: string;
  alt?: string;
  filename?: string;
  width?: number;
  height?: number;
  bytes?: number;
  folder?: string;
}

export interface CloudinaryAsset {
  public_id: string;
  secure_url: string;
  filename: string;
  format: string;
  bytes: number;
  width: number;
  height: number;
  folder: string;
  created_at: string;
  tags: string[];
}

export interface CloudinaryFolder {
  path: string;
  name: string;
  imageCount: number;
}

// Cloudinary configuration
const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'di1mzmlts';
const GALLERY_PREFIX = 'carmen_lorraine_photos/galleries';

/**
 * Generate direct Cloudinary URL for an image
 * No API calls - just URL construction
 */
export const getCloudinaryUrl = (publicId: string, options: {
  width?: number;
  height?: number;
  crop?: 'fill' | 'fit' | 'scale' | 'crop';
  quality?: 'auto' | number;
  format?: 'auto' | 'webp' | 'jpg' | 'png';
} = {}): string => {
  const {
    width,
    height,
    crop = 'fill',
    quality = 'auto',
    format = 'auto'
  } = options;

  let url = `https://res.cloudinary.com/${CLOUD_NAME}/image/upload/`;
  
  // Add transformations
  const transformations = [];
  if (width) transformations.push(`w_${width}`);
  if (height) transformations.push(`h_${height}`);
  if (crop) transformations.push(`c_${crop}`);
  if (quality) transformations.push(`q_${quality}`);
  if (format) transformations.push(`f_${format}`);
  
  if (transformations.length > 0) {
    url += transformations.join(',') + '/';
  }
  
  url += publicId;
  
  return url;
};

/**
 * Generate optimized image URL for different use cases
 */
export const getOptimizedImageUrl = (publicId: string, type: 'thumbnail' | 'card' | 'modal' | 'hero' = 'card'): string => {
  const configs = {
    thumbnail: { width: 200, height: 200, crop: 'fill' as const, quality: 'auto' as const },
    card: { width: 600, height: 450, crop: 'fill' as const, quality: 'auto' as const },
    modal: { width: 1200, height: 900, crop: 'fit' as const, quality: 'auto' as const },
    hero: { width: 2000, height: 1400, crop: 'fill' as const, quality: 'auto' as const }
  };
  
  return getCloudinaryUrl(publicId, configs[type]);
};

/**
 * Static list of uploaded images - replace with your actual uploaded images
 * This prevents API calls while still having access to your images
 * 
 * To add your images:
 * 1. Upload them to Cloudinary with the folder 'carmen_lorraine_photos/galleries'
 * 2. Copy the public_id from Cloudinary
 * 3. Add an entry here with the public_id and other details
 */
export const STATIC_IMAGE_LIST: CloudinaryImage[] = [
  // Example images - replace these with your actual uploaded images
  {
    publicId: 'carmen_lorraine_photos/portrait-1',
    url: `https://res.cloudinary.com/${CLOUD_NAME}/image/upload/carmen_lorraine_photos/portrait-1.jpg`,
    filename: 'portrait-1.jpg',
    alt: 'Portrait 1',
    width: 1200,
    height: 800,
    folder: 'carmen_lorraine_photos/galleries'
  },
  {
    publicId: 'carmen_lorraine_photos/wedding-1',
    url: `https://res.cloudinary.com/${CLOUD_NAME}/image/upload/carmen_lorraine_photos/wedding-1.jpg`,
    filename: 'wedding-1.jpg',
    alt: 'Wedding 1',
    width: 1600,
    height: 1200,
    folder: 'carmen_lorraine_photos/galleries'
  },
  {
    publicId: 'carmen_lorraine_photos/commercial-1',
    url: `https://res.cloudinary.com/${CLOUD_NAME}/image/upload/carmen_lorraine_photos/commercial-1.jpg`,
    filename: 'commercial-1.jpg',
    alt: 'Commercial 1',
    width: 1200,
    height: 800,
    folder: 'carmen_lorraine_photos/galleries'
  },
  {
    publicId: 'carmen_lorraine_photos/event-1',
    url: `https://res.cloudinary.com/${CLOUD_NAME}/image/upload/carmen_lorraine_photos/event-1.jpg`,
    filename: 'event-1.jpg',
    alt: 'Event 1',
    width: 1400,
    height: 900,
    folder: 'carmen_lorraine_photos/galleries'
  },
  {
    publicId: 'carmen_lorraine_photos/nature-1',
    url: `https://res.cloudinary.com/${CLOUD_NAME}/image/upload/carmen_lorraine_photos/nature-1.jpg`,
    filename: 'nature-1.jpg',
    alt: 'Nature 1',
    width: 1200,
    height: 800,
    folder: 'carmen_lorraine_photos/galleries'
  },
  // Add more images as needed...
  // To add your images, follow this format:
  // {
  //   publicId: 'carmen_lorraine_photos/your-image-name',
  //   url: `https://res.cloudinary.com/${CLOUD_NAME}/image/upload/carmen_lorraine_photos/your-image-name.jpg`,
  //   filename: 'your-image-name.jpg',
  //   alt: 'Your Image Description',
  //   width: 1200,
  //   height: 800,
  //   folder: 'carmen_lorraine_photos/galleries'
  // },
];

/**
 * Get all available images (from static list)
 * No API calls - just returns the static list
 */
export const getAllImages = (): CloudinaryImage[] => {
  // For now, return the static list, but this could be enhanced to fetch from Cloudinary API
  return STATIC_IMAGE_LIST.map(img => ({
    ...img,
    secure_url: img.url || getCloudinaryUrl(img.publicId),
    public_id: img.publicId
  }));
};

// Function to fetch actual images from Cloudinary API
export const fetchRealCloudinaryImages = async (): Promise<CloudinaryImage[]> => {
  try {
    return await fetchGalleryCloudinaryImages();
  } catch (error) {
    console.warn('Error fetching scoped Cloudinary images, using static images:', error);
    return getAllImages();
  }
};

export const fetchGalleryCloudinaryImages = async (
  prefix = GALLERY_PREFIX,
  maxResults = 200,
  nextCursor?: string
): Promise<CloudinaryImage[]> => {
  try {
    const params = new URLSearchParams({
      prefix,
      max_results: String(Math.min(Math.max(maxResults, 1), 500)),
      _ts: String(Date.now()),
    });
    if (nextCursor) {
      params.set('next_cursor', nextCursor);
    }
    const response = await fetch(`${API_BASE_URL}/cloudinary/images?${params.toString()}`, {
      cache: 'no-store',
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch Cloudinary assets: ${response.status}`);
    }
    
    const data = await response.json();
    const resources = Array.isArray(data.resources) ? data.resources : [];
    return resources.map((resource: any) => ({
      publicId: resource.public_id,
      url: resource.secure_url,
      filename: resource.filename,
      alt: resource.context?.alt || resource.public_id.split('/').pop()?.replace(/\.[^/.]+$/, '') || 'Image',
      width: resource.width,
      height: resource.height,
      bytes: resource.bytes,
      folder: resource.folder || 'general',
      secure_url: resource.secure_url
    }));
  } catch (error) {
    throw error;
  }
};

export const fetchGalleryCloudinaryFolders = async (): Promise<CloudinaryFolder[]> => {
  const response = await fetch(`${API_BASE_URL}/cloudinary/folders?_ts=${Date.now()}`, {
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch Cloudinary folders: ${response.status}`);
  }

  const data = await response.json();
  const folders = Array.isArray(data.folders) ? data.folders : [];

  return folders.map((folder: any) => ({
    path: String(folder.path || ''),
    name: String(folder.name || folder.path || ''),
    imageCount: Number(folder.imageCount || 0),
  }));
};

/**
 * Get images by folder
 * No API calls - filters the static list
 */
export const getImagesByFolder = (folder: string): CloudinaryImage[] => {
  return STATIC_IMAGE_LIST
    .filter(img => img.folder === folder)
    .map(img => ({
      ...img,
      secure_url: img.url || getCloudinaryUrl(img.publicId),
      public_id: img.publicId
    }));
};

/**
 * Search images by filename or alt text
 * No API calls - searches the static list
 */
export const searchImages = (query: string): CloudinaryImage[] => {
  const lowercaseQuery = query.toLowerCase();
  return STATIC_IMAGE_LIST
    .filter(img => 
      img.filename?.toLowerCase().includes(lowercaseQuery) ||
      img.alt?.toLowerCase().includes(lowercaseQuery) ||
      img.publicId.toLowerCase().includes(lowercaseQuery)
    )
    .map(img => ({
      ...img,
      secure_url: img.url || getCloudinaryUrl(img.publicId),
      public_id: img.publicId
    }));
};

/**
 * Upload widget configuration for adding new images
 * This is the only Cloudinary functionality we keep
 */
export const getUploadWidgetConfig = () => {
  return {
    cloudName: CLOUD_NAME,
    uploadPreset: 'unsigned_upload', // Use unsigned upload preset
    folder: 'carmen_lorraine_photos/galleries',
    cropping: true,
    croppingAspectRatio: 1.5,
    croppingShowDimensions: true,
    resourceType: 'image',
    maxFileSize: 10000000, // 10MB
    maxImageWidth: 4000,
    maxImageHeight: 4000,
    clientAllowedFormats: ['jpg', 'jpeg', 'png', 'webp'],
    theme: 'minimal',
    styles: {
      palette: {
        window: '#FFFFFF',
        sourceBg: '#F4F4F5',
        windowBorder: '#90A0B3',
        tabIcon: '#000000',
        inactiveTabIcon: '#555A5F',
        menuIcons: '#555A5F',
        link: '#0433FF',
        action: '#339933',
        inProgress: '#0433FF',
        complete: '#20B832',
        error: '#EA2727',
        textDark: '#000000',
        textLight: '#FFFFFF'
      },
      fonts: {
        default: null,
        "'Fira Sans', sans-serif": {
          url: 'https://fonts.googleapis.com/css?family=Fira+Sans',
          active: true
        }
      }
    }
  };
};

/**
 * Add a new image to the static list after upload
 * This would typically be called after a successful upload
 */
export const addImageToList = (image: CloudinaryImage): void => {
  STATIC_IMAGE_LIST.push({
    ...image,
    secure_url: image.url || getCloudinaryUrl(image.publicId),
    public_id: image.publicId
  });
};

/**
 * Remove an image from the static list
 */
export const removeImageFromList = (publicId: string): void => {
  const index = STATIC_IMAGE_LIST.findIndex(img => img.publicId === publicId);
  if (index > -1) {
    STATIC_IMAGE_LIST.splice(index, 1);
  }
};

// Legacy function names for compatibility - but they now use static data
export const fetchAllCloudinaryImages = (): Promise<CloudinaryAsset[]> => {
  return Promise.resolve(
    STATIC_IMAGE_LIST.map(img => ({
      public_id: img.publicId,
      secure_url: img.url || getCloudinaryUrl(img.publicId),
      filename: img.filename || img.publicId.split('/').pop() || 'image',
      format: 'jpg',
      bytes: img.bytes || 0,
      width: img.width || 400,
      height: img.height || 300,
      folder: img.folder || 'root',
      created_at: new Date().toISOString(),
      tags: []
    }))
  );
};

export const fetchFolderImages = (folderPrefix: string): Promise<CloudinaryAsset[]> => {
  return Promise.resolve(
    getImagesByFolder(folderPrefix).map(img => ({
      public_id: img.publicId,
      secure_url: img.url || getCloudinaryUrl(img.publicId),
      filename: img.filename || img.publicId.split('/').pop() || 'image',
      format: 'jpg',
      bytes: img.bytes || 0,
      width: img.width || 400,
      height: img.height || 300,
      folder: img.folder || 'root',
      created_at: new Date().toISOString(),
      tags: []
    }))
  );
};

// Clear cache functions (now no-ops since we don't use caching)
export const clearCloudinaryCache = (): void => {
  console.log('Cache clearing not needed - using static image list');
};

export const cacheActualCloudinaryImages = async (): Promise<CloudinaryImage[]> => {
  console.log('Caching not needed - using static image list');
  return STATIC_IMAGE_LIST;
};